<template>
  <div class="grid gap-7">
    <!--
      One segmented control rather than two independent toggles: both of those flipped the same
      boolean, so clicking the tab you were already on switched you to the other one.
    -->
    <div class="cm-segmented" role="tablist" :aria-label="$t('challenges_navigation_match')">
      <button
        type="button"
        role="tab"
        :aria-selected="showShortlist ? 'false' : 'true'"
        :class="{ 'is-active': !showShortlist }"
        @click="showShortlist = false"
      >
        {{ $t('challenge_match_recommendations') }}
        <span class="cm-count" v-if="recommendationCount">{{ recommendationCount }}</span>
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="showShortlist ? 'true' : 'false'"
        :class="{ 'is-active': showShortlist }"
        @click="showShortlist = true"
      >
        <icon name="star" size="14"/>
        {{ $t('challenge_match_shortlist') }}
        <span class="cm-count" v-if="shortListedActors.length">{{ shortListedActors.length }}</span>
      </button>
    </div>

    <!--
      ─────────── Adding an actor by hand ───────────

      Above both views rather than inside the shortlist: it is the one thing on this tab a person
      does rather than reads, and behind a tab nobody found it.
    -->
    <section class="cm-add">
      <div class="tw-flex flex-wrap items-center justify-between gap-3">
        <h3 class="cm-heading">{{ $t('challenge_match_add_actor', { actor: actorLabel }) }}</h3>
        <DsInput
          class="cm-search"
          v-model="actorQuery"
          icon="search"
          is-simplified
          :placeholder="$t('challenge_match_search_placeholder', { actors: actorsLabel })"
          @update:model-value="searchActorsDebounced"
        />
      </div>

      <p class="cm-note" v-if="searchingActors">{{ $t('challenge_match_searching') }}</p>

      <div class="grid gap-2" v-else-if="addableActors.length">
        <div class="cm-result" v-for="actor of addableActors" :key="'result' + actor.id">
          <ActorAvatar
            class="cm-result__logo"
            :actor="actor"
            :actor-logo-url="actor.url"
            :width="36"
          />
          <div class="min-w-0">
            <strong class="block truncate text-sm">{{ actor.name }}</strong>
            <span class="block truncate text-xs text-slate-500" v-if="actor.summary">{{ actor.summary }}</span>
          </div>
          <DsButton
            variant="rounded"
            size="small"
            icon="plus"
            icon-fill-color="white"
            :label="$t('add')"
            :loading="addingActorId === actor.id"
            @click="addActorToShortlist(actor)"
          />
        </div>
      </div>

      <!-- Only once a search has actually run, so the panel is a single line until it is used. -->
      <p class="cm-note" v-else-if="hasSearchedActors">{{ $t('challenge_match_no_results', { query: actorQuery }) }}</p>
      <p class="cm-note" v-else-if="actorQuery.trim().length">
        {{ $t('challenge_match_search_hint', { count: MIN_QUERY_LENGTH }) }}
      </p>
    </section>

    <!-- ─────────── What the matchmaker found ─────────── -->
    <template v-if="!showShortlist">
      <Loading v-if="loadingRecommendations"/>

      <template v-else>
        <section class="grid gap-4">
          <header class="tw-flex flex-wrap items-center justify-between gap-3">
            <h3 class="cm-heading">{{ $t('challenge_recommended_actors_title', { actors: actorsLabel }) }}</h3>
            <!-- Both lists are already loaded, so their filters are instant and cost no request. -->
            <DsInput
              class="cm-search"
              v-model="recommendedQuery"
              icon="search"
              is-simplified
              :placeholder="$t('challenge_match_filter_placeholder')"
              v-if="hasRecommendations"
            />
          </header>

          <template v-if="hasRecommendations">
            <div class="cm-grid" v-if="filteredAgenticActors.length">
              <ActorSuggestionCardNew
                v-for="matchingActor in filteredAgenticActors"
                :key="matchingActor.id"
                :actor="matchingActor"
                :loading="togglingActorId === matchingActor.id"
                @toggle-shortlist-status="toggleActorShortListStatus"
                @manage-invitees="openManageInviteesSidePanel"
              />
            </div>
            <p class="cm-note" v-else>{{ $t('challenge_match_no_results', { query: recommendedQuery }) }}</p>
          </template>

          <p class="cm-note" v-else-if="challenge.matchmakingTaskStatus !== 'completed'">
            {{ $t('challenge_matchmaking_in_progress') }}
          </p>
          <p class="cm-note" v-else>
            {{ $t('challenge_matchmaking_no_recommended_actors', { actors: actorsLabel }) }}
          </p>
        </section>

        <section class="grid gap-4" v-if="interestingActors.length">
          <header class="tw-flex flex-wrap items-center justify-between gap-3">
            <h3 class="cm-heading">{{ $t('challenge_interesting_actors_title', { actors: actorsLabel }) }}</h3>
            <!-- These are already loaded, so the filter is instant and needs no request. -->
            <DsInput
              class="cm-search"
              v-model="interestingQuery"
              icon="search"
              is-simplified
              :placeholder="$t('challenge_match_filter_placeholder')"
            />
          </header>

          <div class="cm-grid" v-if="filteredInterestingActors.length">
            <ActorSuggestionCardNew
              v-for="matchingActor in filteredInterestingActors"
              :key="matchingActor.id"
              :actor="matchingActor"
              :loading="togglingActorId === matchingActor.id"
              @toggle-shortlist-status="toggleActorShortListStatus"
              @manage-invitees="openManageInviteesSidePanel"
            />
          </div>
          <p class="cm-note" v-else>{{ $t('challenge_match_no_results', { query: interestingQuery }) }}</p>
        </section>
      </template>
    </template>

    <!-- ─────────── The shortlist, and adding to it by hand ─────────── -->
    <template v-else>
      <section class="grid gap-4">
        <h3 class="cm-heading">{{ $t('challenge_match_shortlist') }}</h3>

        <Loading v-if="loadingShortlistedActors"/>

        <div class="cm-grid" v-else-if="shortListedActors.length">
          <ActorSuggestionCardNew
            v-for="matchingActor in shortListedActors"
            :key="matchingActor.id"
            :actor="matchingActor"
            :loading="togglingActorId === matchingActor.id"
            @toggle-shortlist-status="toggleActorShortListStatus"
            @manage-invitees="openManageInviteesSidePanel"
          />
        </div>

        <p class="cm-note" v-else>{{ $t('challenge_no_shortlisted_actors', { actors: actorsLabel }) }}</p>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import debounce from 'lodash/debounce'

  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import ActorSuggestionCardNew from '@/Domain/Actor/Components/ActorSuggestionCardNew.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'

  import { getMainActorTypeActorSuggestions } from '@/api/general-search'
  import { htmlText, mapNewGlobalSearchResultToLegacyFormat } from '@/util/helpers'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { addShortListedActor, removeShortListedActor, getMatchingActors, getShortListedActors } from '@/Domain/Challenge/Api/challenges'
  import { convertToFilterObject, stringifyProperties } from '@/store/modules/filters'
  import { transformChallengeActorRecommendationsToChallengeActorArray } from '@/Domain/Challenge/Api/challenge-api-transformers'
  import { ActorChallengeRecommendation } from '@/Domain/Challenge/Types/challenge-types'

  /** Below this the query matches most of the ecosystem, so it is not worth a request. */
  const MIN_QUERY_LENGTH = 3

  /** Enough to choose from without turning the picker into a second results page. */
  const MAX_SEARCH_RESULTS = 8

  export default defineComponent({
    name: 'ChallengeMatchTab',
    mixins: [TranslationsMixin],
    components: {
      ActorAvatar,
      ActorSuggestionCardNew,
      Loading,
    },
    props: {
      challengeId: {
        type: Number,
        required: true,
      },
      challenge: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        showShortlist: false,
        actorRecommendations: [],
        actorRecommendationFilters: [],
        shortListedActors: [],
        loadingRecommendations: false,
        loadingShortlistedActors: false,
        // Per-actor rather than one flag for the lot, which spun every card's star at once.
        togglingActorId: null,
        recommendedQuery: '',
        interestingQuery: '',
        actorQuery: '',
        actorSearchResults: [],
        searchingActors: false,
        hasSearchedActors: false,
        addingActorId: null,
      }
    },
    computed: {
      MIN_QUERY_LENGTH () {
        return MIN_QUERY_LENGTH
      },
      interestingActors (): Array<ActorChallengeRecommendation> {
        return this.actorRecommendations.filter(actor => !actor.suggestedThroughAgenticMatchMaking && !actor.isActorShortListed)
      },
      agenticRecommendedActors (): Array<ActorChallengeRecommendation> {
        return this.actorRecommendations.filter(actor => actor.suggestedThroughAgenticMatchMaking && !actor.isActorShortListed)
      },
      recommendationCount (): number {
        return this.agenticRecommendedActors.length + this.interestingActors.length
      },
      /** Whether the matchmaker has anything to show — the filter hides behind this. */
      hasRecommendations (): boolean {
        return this.agenticRecommendedActors.length > 0 && this.challenge.matchmakingTaskStatus === 'completed'
      },
      filteredAgenticActors (): Array<ActorChallengeRecommendation> {
        return this.filterActors(this.agenticRecommendedActors, this.recommendedQuery)
      },
      filteredInterestingActors (): Array<ActorChallengeRecommendation> {
        return this.filterActors(this.interestingActors, this.interestingQuery)
      },
      /** Search hits that are not on the shortlist already — adding one twice does nothing. */
      addableActors () {
        const shortlisted = new Set(this.shortListedActors.map(actor => actor.id))

        return this.actorSearchResults.filter(actor => !shortlisted.has(actor.id))
      },
    },
    methods: {
      /** Name and description, matched as the reader sees them — the description is stored as HTML. */
      filterActors (actors, query: string) {
        const needle = query.trim().toLowerCase()

        if (!needle) {
          return actors
        }

        return actors.filter(actor => {
          const haystack = `${actor.name || ''} ${htmlText(actor.description)}`

          return haystack.toLowerCase().includes(needle)
        })
      },
      openManageInviteesSidePanel () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'manage-challenge-invitees',
          metaData: this.challenge,
        })
      },

      /* --- the matchmaker's own lists ---------------------------------- */

      fetchMatchingActors () {
        const stringifiedFilterObject = stringifyProperties(convertToFilterObject(this.actorRecommendationFilters))

        this.loadingRecommendations = true

        getMatchingActors(this.challengeId, stringifiedFilterObject)
          .then(result => {
            this.actorRecommendations = transformChallengeActorRecommendationsToChallengeActorArray(result)
          })
          .finally(() => {
            this.loadingRecommendations = false
          })
      },
      fetchShortListedActors () {
        this.loadingShortlistedActors = true

        return getShortListedActors(this.challengeId)
          .then(result => {
            this.shortListedActors = transformChallengeActorRecommendationsToChallengeActorArray(result)
          })
          .catch(error => {
            console.error('Failed to fetch shortlisted actors:', error)
          })
          .finally(() => {
            this.loadingShortlistedActors = false
          })
      },
      async toggleActorShortListStatus (actor) {
        this.togglingActorId = actor.id

        try {
          if (!actor.isActorShortListed) {
            await addShortListedActor(this.challengeId, actor.id)
          } else {
            await removeShortListedActor(this.challengeId, actor.id)
          }

          actor.isActorShortListed = !actor.isActorShortListed

          await this.fetchShortListedActors()
        } catch (error) {
          console.error('Failed to toggle shortlist status:', error)
        } finally {
          this.togglingActorId = null
        }
      },

      /* --- adding an actor by hand ------------------------------------- */

      searchActors () {
        const query = this.actorQuery.trim()

        if (query.length < MIN_QUERY_LENGTH) {
          this.actorSearchResults = []
          this.hasSearchedActors = false
          return
        }

        this.searchingActors = true

        getMainActorTypeActorSuggestions(query)
          .then(result => {
            this.actorSearchResults = mapNewGlobalSearchResultToLegacyFormat([
              ...(result.actors || []),
              ...(result.products || []),
            ])
              // Only an actor this ecosystem holds a record of can be linked to a challenge: the
              // endpoint resolves the elastic id to a SQL one and fails on anything it cannot map.
              .filter(actor => actor.actor_id)
              .slice(0, MAX_SEARCH_RESULTS)
              .map(actor => ({
                ...actor,
                summary: htmlText(actor.short_description).replace(/\s+/g, ' ').trim(),
              }))
          })
          .catch(() => {
            this.actorSearchResults = []
          })
          .finally(() => {
            this.searchingActors = false
            this.hasSearchedActors = true
          })
      },
      searchActorsDebounced: debounce(function () {
        this.searchActors()
      }, 400),
      async addActorToShortlist (actor) {
        this.addingActorId = actor.id

        try {
          await addShortListedActor(this.challengeId, actor.id)

          // If the matchmaker had also suggested this one, it belongs on the shortlist now and not
          // in the recommendations — no need to refetch them to work that out.
          const recommended = this.actorRecommendations.find(item => item.id === actor.id)

          if (recommended) {
            recommended.isActorShortListed = true
          }

          await this.fetchShortListedActors()
        } catch (error) {
          console.error('Failed to add actor to the shortlist:', error)
        } finally {
          this.addingActorId = null
        }
      },

      /* --- invitees ---------------------------------------------------- */

      onInviteesUpdated () {
        this.fetchShortListedActors()
      },
      async onInviteesInvited (actorId) {
        await addShortListedActor(this.challengeId, actorId)
        this.fetchShortListedActors()
      },
    },
    mounted () {
      this.fetchMatchingActors()
      this.fetchShortListedActors()

      this.$bus.on('challengeInviteesUpdated', this.onInviteesUpdated)
      this.$bus.on('challengeInviteesInvited', this.onInviteesInvited)
    },
    beforeUnmount () {
      this.$bus.off('challengeInviteesUpdated', this.onInviteesUpdated)
      this.$bus.off('challengeInviteesInvited', this.onInviteesInvited)
    },
    watch: {
      challengeId () {
        this.recommendedQuery = ''
        this.interestingQuery = ''
        this.actorQuery = ''
        this.actorSearchResults = []
        this.hasSearchedActors = false

        this.fetchMatchingActors()
        this.fetchShortListedActors()
      },
    },
  })
</script>

<style lang="scss" scoped>
  .cm-segmented {
    @apply inline-flex gap-1 rounded-xl border border-gray-100 bg-slate-50 p-1;

    button {
      @apply inline-flex items-center gap-2 rounded-lg border-0 bg-transparent px-3.5 py-2 text-[13px] font-semibold text-slate-500 cursor-pointer transition-colors;

      &:hover {
        @apply text-slate-900;
      }

      &.is-active {
        @apply bg-white text-primary;

        box-shadow: 0 1px 2px rgba(16, 24, 40, .06);
      }

      &:focus-visible {
        outline: 2px solid var(--primary-community);
        outline-offset: 2px;
      }
    }

    /* `star` hardcodes its own fill, which beats the `currentColor` the Icon component sets on the
       container — the same override the app's iconColor() mixin does for `.button`. */
    :deep(.svg-icon path) {
      fill: currentColor !important;
    }
  }

  .cm-count {
    @apply rounded-full bg-slate-200 px-1.5 text-[11px] font-semibold text-slate-600;

    .is-active & {
      @apply bg-primary-extra-lightest text-primary;
    }
  }

  /* A panel rather than a bare field, so an always-visible control does not read as page chrome. */
  .cm-add {
    @apply grid gap-3 rounded-xl border border-gray-100 bg-slate-50 p-4;
  }

  .cm-heading {
    @apply m-0 text-[13px] font-semibold uppercase tracking-[0.08em] text-slate-400;
  }

  .cm-note {
    @apply m-0 text-sm text-slate-500;
  }

  .cm-search {
    @apply w-full sm:w-64;
  }

  /* A fixed card width in a wrapping row left ragged gaps; the grid fills the measure it has. */
  .cm-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 1rem;
  }

  .cm-result {
    @apply items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm;

    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  /* Same square white tile the detail page gives a logo: ActorAvatar leaves the image at
     `min-width: 100%` with no height cap, so a wide logo runs straight out of its box. */
  .cm-result__logo {
    @apply shrink-0 overflow-hidden rounded-lg bg-white;

    :deep(img) {
      width: 100%;
      height: 100%;
      padding: 2px;
      box-sizing: border-box;
      object-fit: contain;
    }
  }
</style>
